import { useState, useEffect, useCallback } from 'react';

const slides = [
  {
    image: 'https://ziersteine.de/wp-content/uploads/2019/02/slider-arabella.jpg',
    tag: 'Arabella',
    subtitle: 'Italien · Marmor',
  },
  {
    image: 'https://ziersteine.de/wp-content/uploads/2019/02/slider-jura-kalkstein.jpg',
    tag: 'Jura',
    subtitle: 'Deutschland · Kalkstein',
  },
  {
    image: 'https://ziersteine.de/wp-content/uploads/2019/02/slider-glanzkies-tiger-eye.jpg',
    tag: 'Glanzkies Tiger Eye',
    subtitle: 'China · Kalkstein-gelackt',
  },
  {
    image: 'https://ziersteine.de/wp-content/uploads/2019/02/slider-rouge-royal.jpg',
    tag: 'Rouge Royal',
    subtitle: 'Griechenland · Marmor',
  },
  {
    image: 'https://ziersteine.de/wp-content/uploads/2019/02/slider-kristall-gelb.jpg',
    tag: 'Kristall Gelb',
    subtitle: 'Italien · Marmor',
  },
  {
    image: 'https://ziersteine.de/wp-content/uploads/2019/02/slider-canadian-slate-green.jpg',
    tag: 'Canadian Slate Green',
    subtitle: 'Großbritannien · Schiefer',
  },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (animating) return;
      setAnimating(true);
      setTimeout(() => {
        setCurrent(index);
        setAnimating(false);
      }, 400);
    },
    [animating]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative w-full h-screen overflow-hidden">
      {/* Background Images */}
      {slides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.tag}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 w-full">
        <div
          className={`transition-all duration-500 ${animating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}
        >
          <span className="inline-block px-4 py-1 rounded-full border border-white/40 text-white/80 text-xs tracking-widest uppercase mb-6 backdrop-blur-sm">
            {slides[current].subtitle}
          </span>
          <h1 className="text-white font-extralight leading-tight mb-3" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>
            {slides[current].tag}
          </h1>
          <p className="text-white/70 text-lg font-light mb-3" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
            Natursteine aus aller Welt
          </p>
          <p className="text-white/60 text-base font-light max-w-xl mx-auto mb-10">
            Ihr Partner für Ziersteine — zeitlose Eleganz für Garten, Vorgarten & Außenanlage
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <button
              onClick={() => handleScroll('#portfolio')}
              className="px-7 py-3 bg-white text-stone-900 rounded-full text-sm font-medium hover:bg-white/90 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Portfolio ansehen
            </button>
            <button
              onClick={() => handleScroll('#contact')}
              className="px-7 py-3 border border-white/40 text-white rounded-full text-sm font-medium hover:bg-white/10 transition-all duration-200 cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              Kontakt aufnehmen
            </button>
          </div>
        </div>
      </div>

      {/* Dot Navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`rounded-full transition-all duration-300 cursor-pointer ${
              i === current ? 'w-7 h-2 bg-white' : 'w-2 h-2 bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Scroll Hint */}
      <div className="absolute bottom-8 right-8 z-10 flex flex-col items-center gap-2 text-white/40">
        <span className="text-xs tracking-widest rotate-90 origin-center" style={{ writingMode: 'vertical-rl' }}>Scrollen</span>
        <i className="ri-arrow-down-line text-sm animate-bounce" />
      </div>
    </section>
  );
}
